import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Toast.css";

export type ToastTone = "success" | "error" | "info" | "attention" | "warning";

export interface ToastProps extends HTMLAttributes<HTMLDivElement> {
  tone?: ToastTone;
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const outlineProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

const DefaultIcon = ({ tone }: { tone: ToastTone }) => {
  switch (tone) {
    case "success":
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12.5l3 3 5-6" />
        </svg>
      );
    case "attention":
    case "warning":
      return (
        <svg {...outlineProps}>
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          <path d="M12 8v3" />
          <path d="M12 14h.01" />
        </svg>
      );
    case "error":
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 7v6" />
          <path d="M12 16.5h.01" />
        </svg>
      );
    case "info":
    default:
      return (
        <svg {...outlineProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 11v6" />
          <path d="M12 7h.01" />
        </svg>
      );
  }
};

export const Toast = forwardRef<HTMLDivElement, ToastProps>(function Toast(
  {
    tone = "info",
    icon,
    onDismiss,
    dismissLabel = "Dismiss",
    className,
    children,
    role = "status",
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      role={role}
      className={cx("go4-toast", `go4-toast--${tone}`, className)}
      {...rest}
    >
      <span className="go4-toast__icon">{icon ?? <DefaultIcon tone={tone} />}</span>
      <span className="go4-toast__message">{children}</span>
      {onDismiss ? (
        <button
          type="button"
          aria-label={dismissLabel}
          className="go4-toast__dismiss"
          onClick={onDismiss}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
});
